"use client";

import { useLang } from "@/contexts/language-context";
import { AnimatePresence, motion } from "framer-motion";
import { CloudOff, Wifi, WifiOff } from "lucide-react";
import { useEffect, useState } from "react";
import Link from "next/link";

type Phase = "online" | "drop" | "offline" | "recover" | "sync";

const PHASES: Phase[] = ["online", "drop", "offline", "recover", "sync"];
const PHASE_DURATION = 1800;

export default function FoodTruckQsrFestivalMode() {
  const { t } = useLang();
  const [phase, setPhase] = useState<Phase>("online");
  const [orders, setOrders] = useState(12);
  const [queued, setQueued] = useState(0);

  useEffect(() => {
    let i = 0;
    const tick = setInterval(() => {
      i = (i + 1) % PHASES.length;
      const p = PHASES[i];
      setPhase(p);
      if (p === "online") {
        setOrders(12);
        setQueued(0);
      }
      if (p === "offline") {
        setQueued(0);
      }
    }, PHASE_DURATION);
    return () => clearInterval(tick);
  }, []);

  // Order counter during offline period
  useEffect(() => {
    if (phase !== "offline") return;
    const id = setInterval(() => setQueued((q) => q + 1), 320);
    return () => clearInterval(id);
  }, [phase]);

  // On sync, queued orders flush into orders
  useEffect(() => {
    if (phase !== "sync") return;
    const id = setTimeout(() => {
      setOrders((o) => o + queued);
      setQueued(0);
    }, 600);
    return () => clearTimeout(id);
  }, [phase, queued]);

  const isOffline = phase === "drop" || phase === "offline" || phase === "recover";

  return (
    <section className="py-24 px-6 bg-gradient-to-b from-[#FAFAFA] to-white">
      <div className="max-w-[1100px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.1fr] gap-12 items-center">
          {/* Left: copy */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-redy-orange mb-4">
                {t("Festival mode", "Modo festival")}
              </div>
              <h2 className="text-[40px] md:text-[60px] font-black leading-[1.02] tracking-tight text-redy-charcoal mb-5">
                {t("Where signal dies,", "Donde muere la señal,")}
                <br />
                <span className="redy-text-gradient">
                  {t("the cash line keeps moving.", "la fila de efectivo sigue.")}
                </span>
              </h2>
              <p className="text-lg text-redy-secondary leading-relaxed mb-6 max-w-[500px]">
                {t(
                  "Parking lot. Concert. State fair. WiFi drops, your hotspot crawls. The iPad switches to cash mode and keeps ringing cash sales, saved on the device. Cards need a signal — they're back the moment it returns.",
                  "Estacionamiento. Concierto. Feria. El WiFi se cae, tu hotspot se arrastra. El iPad pasa a modo efectivo y sigue cobrando en efectivo, guardando las ventas en el dispositivo. La tarjeta necesita señal — vuelve en cuanto regresa."
                )}
              </p>
              <p className="text-base text-redy-charcoal/80 leading-relaxed font-medium max-w-[500px]">
                {t(
                  "When you're back online, those cash sales sync in the background. Any sale that can't sync is flagged for review.",
                  "Cuando vuelves online, esas ventas en efectivo se sincronizan en background. Cualquier venta que no pueda sincronizar queda marcada para revisión."
                )}
              </p>

              <div className="mt-8">
                <Link
                  href="/features/works-offline"
                  className="inline-flex items-center gap-2 text-sm font-bold text-redy-charcoal hover:text-redy-orange transition-colors"
                >
                  {t("How offline mode works →", "Cómo funciona el modo offline →")}
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Right: device with signal indicator */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="relative"
          >
            <div className="relative bg-redy-charcoal rounded-[28px] p-4 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.3)]">
              <div className="bg-[#FDFCFA] rounded-[20px] p-6 min-h-[400px] flex flex-col">
                {/* Status bar */}
                <div className="flex items-center justify-between mb-6">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-redy-charcoal/50">
                    {t("Redy POS · Counter", "Redy POS · Mostrador")}
                  </div>
                  <motion.div
                    animate={{
                      backgroundColor: isOffline
                        ? "#FEE2E2"
                        : phase === "sync"
                        ? "#FED7AA"
                        : "#DCFCE7",
                    }}
                    transition={{ duration: 0.4 }}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                  >
                    {isOffline ? (
                      <WifiOff className="w-3 h-3 text-redy-red" strokeWidth={2.5} />
                    ) : (
                      <Wifi className="w-3 h-3 text-emerald-700" strokeWidth={2.5} />
                    )}
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider ${
                        isOffline
                          ? "text-redy-red"
                          : phase === "sync"
                          ? "text-redy-orange"
                          : "text-emerald-700"
                      }`}
                    >
                      {isOffline
                        ? t("Offline", "Offline")
                        : phase === "sync"
                        ? t("Syncing…", "Sincronizando…")
                        : t("Online", "Online")}
                    </span>
                  </motion.div>
                </div>

                {/* Signal bars animation */}
                <div className="flex items-end gap-1 h-8 mb-6">
                  {[0, 1, 2, 3, 4].map((b) => {
                    const active =
                      (!isOffline && b < 5) ||
                      (phase === "drop" && b < 2) ||
                      (phase === "recover" && b < 3);
                    return (
                      <motion.div
                        key={b}
                        animate={{
                          height: 8 + b * 5,
                          backgroundColor: active
                            ? phase === "sync"
                              ? "#F4722B"
                              : "#1A1A1A"
                            : "#E5E5E5",
                        }}
                        transition={{ duration: 0.3 }}
                        className="w-2 rounded-sm"
                      />
                    );
                  })}
                </div>

                {/* Orders counter */}
                <div className="flex-1 flex flex-col items-center justify-center">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-redy-charcoal/50 mb-2">
                    {t("Orders today", "Órdenes hoy")}
                  </div>
                  <motion.div
                    key={orders}
                    initial={{ scale: 0.9, opacity: 0.5 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="font-mono tabular-nums text-[88px] font-black leading-none text-redy-charcoal"
                  >
                    {String(orders).padStart(3, "0")}
                  </motion.div>

                  <AnimatePresence>
                    {(phase === "offline" || phase === "recover") && queued > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="mt-4 inline-flex items-center gap-2 bg-redy-charcoal/5 text-redy-charcoal px-3 py-1.5 rounded-full text-xs font-bold"
                      >
                        <CloudOff className="w-3.5 h-3.5 text-redy-red" />
                        <span>
                          +{queued}{" "}
                          {t("cash sales saved offline", "ventas en efectivo guardadas offline")}
                        </span>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <AnimatePresence>
                    {phase === "sync" && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="mt-4 inline-flex items-center gap-2 bg-redy-orange text-white px-3 py-1.5 rounded-full text-xs font-bold"
                      >
                        ✓ {t("All synced", "Todo sincronizado")}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div className="mt-6 pt-4 border-t border-black/5 text-center">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-redy-charcoal/40">
                    {phase === "offline"
                      ? t("No signal · cash only", "Sin señal · solo efectivo")
                      : phase === "sync"
                      ? t("Back online · catching up", "Vuelve online · poniéndose al día")
                      : t("Live · all systems go", "En vivo · todo OK")}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
